import React from 'react';
import { motion } from 'framer-motion';
import { Shield, Lock, CreditCard, BookOpen, Zap, Star } from 'lucide-react';
import { CTAButton, CHECKOUT_URL } from './Shared';
import { FeatherQuill, Flame, MoonPhase, LotusBud } from './HealingIcons';
const heroImage = {
  src: 'https://cdn.magicpatterns.com/uploads/arWandrUcABUonkcP94efi/Mockup_con_buena_calidad_sin_fondo.png',
  alt: 'Mega Pack Chi Kung - Sistema clínico completo'
};
export const Hero = () => {
  return <div className="relative overflow-hidden pb-8 md:pt-20 md:pb-12 bg-transparent pt-[6px]" data-id="element-136">
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-brand-purpleLight/10 rounded-full blur-[140px] -z-10 pointer-events-none" data-id="element-137"></div>

      <FeatherQuill className="hidden md:block absolute top-32 left-[6%] w-8 h-8 opacity-30 animate-float-soft -z-10" color="#7C8A4E" style={{
      transform: 'rotate(20deg)'
    }} data-id="element-138" />
      <MoonPhase className="hidden md:block absolute top-48 right-[8%] w-10 h-10 opacity-30 animate-float-soft -z-10" color="#7C8A4E" style={{
      transform: 'rotate(-25deg)',
      animationDelay: '1.2s'
    }} data-id="element-139" />
      <LotusBud className="hidden md:block absolute bottom-40 left-[4%] w-12 h-12 opacity-40 animate-float-soft -z-10" color="#C9992F" style={{
      animationDelay: '2s'
    }} data-id="element-140" />
      <Flame className="hidden md:block absolute bottom-32 right-[5%] w-8 h-8 opacity-50 animate-float-soft animate-ember -z-10" color="#C0752A" style={{
      transform: 'rotate(15deg)',
      animationDelay: '0.6s'
    }} data-id="element-141" />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-id="element-142">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.6
      }} className="bg-transparent space-y-[0px]" data-id="element-143">
          <div className="inline-flex items-center justify-center bg-white/80 backdrop-blur-sm border border-brand-purple/30 px-4 py-1.5 rounded-full mb-3 md:mb-6 gap-2 shadow-sm pt-[2px] pb-[2px] pl-[10px] pr-[10px]" data-id="element-144">
            <span className="text-sm" data-id="element-145">🌿</span>
            <span className="font-serif font-semibold tracking-wider text-xs md:text-sm text-brand-purpleDark" data-id="element-146">
              LUMEN AKASHA™
            </span>
          </div>

          <div className="relative inline-block mb-3 md:mb-4 max-w-4xl mx-auto mt-1 md:mt-4 text-[#171616]" data-id="element-147">
            <div className="absolute inset-0 -z-10 blur-3xl opacity-60 pointer-events-none" data-id="element-148">
              <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[110%] h-[140%] bg-gradient-to-r from-brand-purpleLight/30 via-brand-gold/20 to-brand-purple/30 rounded-full" data-id="element-149" />
            </div>

            <span className="sparkle absolute -top-2 left-[8%] text-brand-purple text-sm md:text-xl" style={{
            animationDelay: '0s'
          }} data-id="element-150">
              ✦
            </span>
            <span className="sparkle absolute top-1 right-[6%] text-brand-gold text-xs md:text-lg" style={{
            animationDelay: '0.8s'
          }} data-id="element-151">
              ✧
            </span>
            <span className="sparkle absolute -bottom-1 left-[20%] text-brand-purpleLight text-xs md:text-base" style={{
            animationDelay: '1.6s'
          }} data-id="element-152">
              ✦
            </span>
            <span className="sparkle absolute bottom-2 right-[18%] text-brand-purple text-sm md:text-lg" style={{
            animationDelay: '2.2s'
          }} data-id="element-153">
              ✧
            </span>

            <h1 className="sm:text-4xl md:text-6xl lg:text-7xl text-[28px] font-[700] leading-tight tracking-tight" style={{
            fontFamily: "'Cinzel', serif"
          }} data-id="element-154">
              <span className="headline-shine font-[900] text-[24px] md:text-6xl lg:text-7xl" data-id="element-155">
                SESIONES MÁS EFECTIVAS Y SIN IMPROVISAR.
              </span>
              <br data-id="element-156" />
              <span className="block mt-1.5 md:mt-2 font-semibold md:text-lg lg:text-xl text-[#332D2D] tracking-normal text-[18px] font-sans" data-id="element-157">
                El conocimiento ya lo tenés. El sistema para aplicarlo, ahora
                también.✨
              </span>
            </h1>
          </div>
          <p className="hidden md:block md:text-2xl lg:text-3xl mb-8 font-bold font-serif text-[14px] text-[#332D2D]" data-id="element-158">
            Dejá de improvisar frente a tus alumnos y recuperá tu tiempo libre
            con el sistema de sesiones pre-diseñadas que hace el trabajo pesado
            por vos.
          </p>

          <div className="relative z-10 w-full max-w-[420px] sm:max-w-md md:max-w-xl lg:max-w-2xl xl:max-w-3xl mx-auto mb-2 flex justify-center mt-1 md:mt-6" data-id="element-159">
            <div className="relative overflow-hidden rounded-2xl bg-transparent w-full mx-auto aspect-[5/4] md:aspect-[4/3]" style={{
            maxHeight: '38vh'
          }} data-id="element-160">
              <motion.img src={heroImage.src} alt={heroImage.alt} initial={{
              opacity: 0,
              scale: 0.95
            }} animate={{
              opacity: 1,
              scale: 1
            }} transition={{
              duration: 0.6,
              ease: 'easeOut'
            }} className="absolute inset-0 w-full h-full object-cover object-center mx-auto drop-shadow-2xl" draggable={false} fetchpriority="high" decoding="async" data-id="element-161" />
            </div>
          </div>

          <div className="max-w-3xl mx-auto mb-3 md:mb-8 mt-2 md:mt-8 space-y-1 md:space-y-3 px-2" data-id="element-162">
            <h2 className="text-[15px] md:text-2xl lg:text-3xl font-bold font-serif leading-tight md:leading-relaxed uppercase text-[#4D3A32]" data-id="element-163">
              MEGA PACK CHI KUNG™
            </h2>
            <p className="hidden sm:block md:text-xl lg:text-2xl font-bold font-serif text-[#312A28] text-[14px]" data-id="element-164">
              El recurso N°1 para terapeutas que trabajan con Chi Kung — Todos
              los niveles y cuadros clínicos
            </p>
            <p className="hidden md:block text-[11px] md:text-base lg:text-lg text-brand-text font-sans" data-id="element-165">
              Sistema integral de sesiones pre-diseñadas + blindaje del
              terapeuta + planificación anual + herramientas clínicas listas
              para usar desde hoy.
            </p>
          </div>

          <div className="flex flex-col items-center space-y-2.5 md:space-y-6" data-id="element-166">
            <p className="max-w-md mx-auto font-serif font-medium md:text-base text-brand-purpleDark px-2 text-[18px]" data-id="element-167">
              Pasá de la planificación caótica a una práctica clínica
              profesional hoy mismo. 🧘‍♂️
            </p>

            <CTAButton variant="purple" href={CHECKOUT_URL} className="text-base md:text-xl px-6 md:px-10 py-4 md:py-5 cta-shimmer relative overflow-hidden" data-id="element-168">
              <span className="relative z-10" data-id="element-169">→ QUIERO MI MEGA PACK AHORA</span>
            </CTAButton>

            <div className="flex flex-wrap justify-center items-center gap-x-4 gap-y-2 text-xs md:text-sm text-brand-muted" data-id="element-170">
              <div className="flex items-center" data-id="element-171">
                <Shield className="w-3.5 h-3.5 md:w-4 md:h-4 mr-1.5 text-brand-purple" data-id="element-172" />{' '}
                Garantía 7 días
              </div>
              <div className="flex items-center" data-id="element-173">
                📥 Listo para imprimir o usar en tablet
              </div>
              <div className="flex items-center" data-id="element-174">
                <Lock className="w-3.5 h-3.5 md:w-4 md:h-4 mr-1.5 text-brand-purple" data-id="element-175" />{' '}
                Pago seguro
              </div>
              <div className="flex items-center" data-id="element-176">
                <CreditCard className="w-3.5 h-3.5 md:w-4 md:h-4 mr-1.5 text-brand-purple" data-id="element-177" />{' '}
                Mercado Pago
              </div>
            </div>
          </div>

          <div className="flex flex-wrap items-center justify-center gap-1.5 md:gap-3 mt-8 mb-2 md:mb-12 text-xs md:text-sm font-medium px-2" data-id="element-178">
            <div className="inline-flex items-center bg-white px-2 md:px-3 py-1.5 md:py-2 rounded-full border border-brand-purple/25 whitespace-nowrap shadow-sm text-[9px] md:text-sm" data-id="element-179">
              <BookOpen className="w-3 h-3 md:w-4 md:h-4 mr-1 md:mr-1.5 text-brand-purple shrink-0" data-id="element-180" />
              100% PDF
            </div>
            <div className="inline-flex items-center bg-white px-2 md:px-3 py-1.5 md:py-2 rounded-full border border-brand-purple/25 whitespace-nowrap shadow-sm text-[9px] md:text-sm" data-id="element-181">
              <Zap className="w-3 h-3 md:w-4 md:h-4 mr-1 md:mr-1.5 text-brand-purple shrink-0" data-id="element-182" />
              Acceso en 5 min
            </div>
            <div className="inline-flex items-center bg-white px-2 md:px-3 py-1.5 md:py-2 rounded-full border border-brand-purple/25 whitespace-nowrap shadow-sm text-[9px] md:text-sm" data-id="element-183">
              <span className="mr-1 md:mr-1.5 text-brand-purple shrink-0" data-id="element-184">
                🌿
              </span>
              Todos los niveles
            </div>
          </div>
        </motion.div>
      </div>
    </div>;
};